import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BookingService } from '../../../core/api/booking.service';
import { parseApiDateTime, toApiDateTime } from '../../../shared/utils/date.utils';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';

@Component({
  selector: 'app-booking-edit',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule,
    MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule,
    MatSnackBarModule, MatProgressSpinnerModule,
    DatetimeInputComponent,
  ],
  templateUrl: './booking-edit.component.html',
  styleUrl: './booking-edit.component.scss',
})
export class BookingEditComponent implements OnInit {
  form: FormGroup;
  loading = true;
  submitting = false;
  private _bookingId: number | null = null;
  get bookingId(): number | null {
    return this._bookingId;
  }

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private bookingService: BookingService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      startDate: [null as Date | null, Validators.required],
      endDate: [null as Date | null, Validators.required],
      depositAmount: ['', [optionalPattern(/^\d+([.,]\d{1,2})?$/)]],
      rentalAmount: ['', [optionalPattern(/^\d+([.,]\d{1,2})?$/)]],
      notes: [''],
    });
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/bookings']);
      return;
    }
    this._bookingId = id;
    this.bookingService.get(id).subscribe({
      next: (b) => {
        // CVA-компонент `app-datetime-input` сам парсит ISO/DRF-строки от backend
        // в `Date | null` через `splitDateTimeToParts`.
        this.form.patchValue({
          startDate: b.start_date ?? null,
          endDate: b.end_date ?? null,
          depositAmount: b.deposit_amount || '',
          rentalAmount: b.rental_amount || '',
          notes: b.notes || '',
        });
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Бронирование не найдено', 'ОК', { duration: 3000 });
        this.router.navigate(['/bookings']);
      },
    });
  }

  goBack(): void {
    if (this._bookingId) {
      this.router.navigate(['/bookings', this._bookingId]);
    } else {
      this.router.navigate(['/bookings']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting || !this._bookingId) return;

    const v = this.form.value;
    const startDate = v.startDate ? parseApiDateTime(v.startDate) : null;
    const endDate = v.endDate ? parseApiDateTime(v.endDate) : null;

    if (!startDate || !endDate) {
      this.form.markAllAsTouched();
      return;
    }

    // Сравниваем Date-объекты напрямую, не зависая от строкового
    // сравнения `dd.MM.yyyy HH:mm` (лексикографически корректно, но явно).
    if (endDate < startDate) {
      this.form.get('endDate')?.setErrors({ dateRange: true });
      return;
    }

    this.submitting = true;
    const data = {
      start_date: toApiDateTime(startDate),
      end_date: toApiDateTime(endDate),
      deposit_amount: (this.form.value.depositAmount || '').toString().replace(',', '.'),
      rental_amount: (this.form.value.rentalAmount || '').toString().replace(',', '.'),
      notes: this.form.value.notes || '',
    };
    this.bookingService.update(this._bookingId, data).subscribe({
      next: () => {
        this.snackBar.open('Бронирование сохранено', 'ОК', { duration: 3000 });
        this.router.navigate(['/bookings', this._bookingId]);
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          const nonField = errorData.all || errorData.non_field_errors;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : 'Ошибка сохранения';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private readonly apiFieldToControl: Record<string, string> = {
    start_date: 'startDate',
    end_date: 'endDate',
    deposit_amount: 'depositAmount',
    rental_amount: 'rentalAmount',
    notes: 'notes',
  };

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}

function optionalPattern(pattern: RegExp) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === '' || control.value == null) return null;
    return Validators.pattern(pattern)(control);
  };
}
